import { Order } from "@amzn/ito-client";
import { EMPTY_STRING, FREE_STRING, NOT_APPLICABLE_STRING } from "common/types";

export interface OrderData {
  orderStatus: string;
  orderVersion: string;
  orderPrice: string;
  costCenter: string;
  businessJustification: string;
  carCode: string;
  requesterName: string;
  shippingAddress: string;
  phoneNumber: string;
  serialNumber: string;
  assetTag: string;
  poNumber: string;
  shippingCarrier: string;
  trackingNumber: string;
  deliveryInstructions: string;
  hopId: string;
  returnedDeviceSerialNumber: string;
  securityTapeSerialNumber: string;
}

interface OrderDetails {
  checkoutInfo?: {
    costCenter?: string;
    businessJustification?: string;
    carCode?: string;
  };
}

export const extractOrderData = (order: Order): OrderData => {
  // ITO Order Status
  const orderStatus = order.status || NOT_APPLICABLE_STRING;

  const firstItem = order.items && order.items.length > 0 ? order.items[0] : undefined;

  // Extract order details
  const orderVersion = firstItem ? (firstItem.itemInformation as any)?.name : NOT_APPLICABLE_STRING;

  const orderDetails = order.details as OrderDetails;
  const orderPrice =
    order.total && order.total.amount !== undefined && order.total.currency
      ? order.total.amount === 0
        ? FREE_STRING
        : `${order.total.currency} ${order.total.amount}`
      : NOT_APPLICABLE_STRING;

  const costCenter = orderDetails?.checkoutInfo?.costCenter || NOT_APPLICABLE_STRING;
  const businessJustification = orderDetails?.checkoutInfo?.businessJustification || NOT_APPLICABLE_STRING;
  const carCode = orderDetails?.checkoutInfo?.carCode || NOT_APPLICABLE_STRING;

  // Extract shipping details
  const requesterName =
    order.requester && order.requester.basicInfo
      ? `${order.requester.basicInfo.firstName || EMPTY_STRING} ${
          order.requester.basicInfo.lastName || EMPTY_STRING
        }`.trim()
      : NOT_APPLICABLE_STRING;

  const shippingAddress = order.deliveryTo
    ? [
        order.deliveryTo.addressLineData
          ? order.deliveryTo.addressLineData.filter((line) => line && line.trim() !== EMPTY_STRING).join(", ")
          : null,
        order.deliveryTo.city && order.deliveryTo.city.trim() !== EMPTY_STRING ? order.deliveryTo.city : null,
        order.deliveryTo.citySubdivision1 && order.deliveryTo.citySubdivision1.trim() !== EMPTY_STRING
          ? order.deliveryTo.regionSubdivision1
          : null,
        order.deliveryTo.country && order.deliveryTo.country.trim() !== EMPTY_STRING ? order.deliveryTo.country : null,
        order.deliveryTo.postalCode && order.deliveryTo.postalCode.trim() !== EMPTY_STRING
          ? order.deliveryTo.postalCode
          : null,
      ]
        .filter((field) => field && field.toString().trim() !== EMPTY_STRING)
        .join(", ")
    : NOT_APPLICABLE_STRING;

  const phoneNumber =
    order.deliveryTo && order.deliveryTo.phoneNumber ? `+${order.deliveryTo.phoneNumber}` : NOT_APPLICABLE_STRING;

  const deliveryInstructions = order.deliveryTo?.deliveryInstructions || NOT_APPLICABLE_STRING;

  // Extract hardware details
  const serialNumber = firstItem
    ? (firstItem.details as any)?.hopOrders?.[0]?.serialNumber || NOT_APPLICABLE_STRING
    : NOT_APPLICABLE_STRING;

  const assetTag = firstItem
    ? (firstItem.details as any)?.hopOrders?.[0]?.assetTag || NOT_APPLICABLE_STRING
    : NOT_APPLICABLE_STRING;

  const trackingNumber = firstItem
    ? (firstItem.details as any)?.hopOrders?.[0]?.trackingNumber || NOT_APPLICABLE_STRING
    : NOT_APPLICABLE_STRING;

  const poNumber = firstItem ? (firstItem.details as any)?.poNumber || NOT_APPLICABLE_STRING : NOT_APPLICABLE_STRING;

  const shippingCarrier = firstItem
    ? (firstItem.details as any)?.hopOrders?.[0]?.shippingCarrier || NOT_APPLICABLE_STRING
    : NOT_APPLICABLE_STRING;

  const hopId = firstItem
    ? (firstItem.details as any)?.hopOrders?.[0]?.identifier || NOT_APPLICABLE_STRING
    : NOT_APPLICABLE_STRING;

  const returnedDeviceSerialNumber = firstItem
    ? (firstItem.details as any)?.returnedDeviceSerialNumber || NOT_APPLICABLE_STRING
    : NOT_APPLICABLE_STRING;

  const securityTapeSerialNumber = firstItem
    ? (firstItem.details as any)?.hopOrders?.[0]?.securityTapeSerialNumber || NOT_APPLICABLE_STRING
    : NOT_APPLICABLE_STRING;

  return {
    orderStatus,
    orderVersion,
    orderPrice,
    costCenter,
    businessJustification,
    carCode,
    requesterName,
    shippingAddress,
    phoneNumber,
    serialNumber,
    assetTag,
    trackingNumber,
    poNumber,
    shippingCarrier,
    deliveryInstructions,
    hopId,
    returnedDeviceSerialNumber,
    securityTapeSerialNumber,
  };
};
